import { Checkbox } from '@finn/design-system/atoms/checkbox';
import {
  getAdditionalProtectionAddons,
  getAppliedAdditionalProtectionAddons,
  useGetAddons,
} from '@finn/platform-modules';
import {
  MINOR_DAMAGE_PROTECTION_ADDON_ID,
  TIRE_AND_WINDSHIELD_PROTECTION_ADDON_ID,
} from '@finn/ua-constants';
import { Features, useIsABVariant } from '@finn/ua-featureflags';
import {
  TrackComponentView,
  TrackingEventName,
  useTrackingStore,
} from '@finn/ua-tracking';
import { cn, formatEuro, useIntl } from '@finn/ui-utils';
import { useEffect, useMemo } from 'react';

type Props = {
  additionalProtections: string[];
  setAdditionalProtections: (protections: string[]) => void;
  isZeroDeductible: boolean;
  vehicleId?: number | string;
  dealId?: number;
  isCheckoutStep?: boolean;
};

export const AdditionalProtection = ({
  additionalProtections,
  setAdditionalProtections,
  isZeroDeductible,
  vehicleId,
  dealId,
  isCheckoutStep,
}: Props) => {
  const intl = useIntl();
  const track = useTrackingStore((state) => state.track);
  const isMinorDamageExp = useIsABVariant(Features.ExpMinorDamageAddon);

  const { data: addonsData } = useGetAddons({
    vehicleId,
    dealId,
  });

  let addonToRemove = '';

  // only apply logic for checkout step and not in upgrade modal
  if (isCheckoutStep) {
    // haven't applied yet exclude based on variant
    if (additionalProtections.length === 0) {
      addonToRemove = isMinorDamageExp
        ? TIRE_AND_WINDSHIELD_PROTECTION_ADDON_ID
        : MINOR_DAMAGE_PROTECTION_ADDON_ID;
    } else {
      // already applied so ignore variant and remove the other one
      addonToRemove = additionalProtections.includes(
        TIRE_AND_WINDSHIELD_PROTECTION_ADDON_ID
      )
        ? MINOR_DAMAGE_PROTECTION_ADDON_ID
        : TIRE_AND_WINDSHIELD_PROTECTION_ADDON_ID;
    }
  }

  const additionalProtectionAddons = useMemo(
    () =>
      getAdditionalProtectionAddons(addonsData).filter(
        (addon) => addon.addon_id !== addonToRemove
      ),
    [addonsData, addonToRemove]
  );

  const appliedProtections = useMemo(
    () => getAppliedAdditionalProtectionAddons(addonsData),
    [addonsData]
  );

  useEffect(() => {
    let addonIdsToApply = appliedProtections.map((addon) => addon.addon_id);
    if (isZeroDeductible) {
      addonIdsToApply = addonIdsToApply.filter(
        (addonId) =>
          ![
            TIRE_AND_WINDSHIELD_PROTECTION_ADDON_ID,
            MINOR_DAMAGE_PROTECTION_ADDON_ID,
          ].includes(addonId)
      );
    }
    setAdditionalProtections(addonIdsToApply);
  }, [appliedProtections, setAdditionalProtections, isZeroDeductible]);

  if (!additionalProtectionAddons?.length) return null;

  return (
    <TrackComponentView name="additionalProtection">
      <div className="mt-4 flex flex-col gap-4">
        <p className="body-16-semibold">
          {intl.formatMessage('experiments.additionalProtection.title')}
        </p>
        <div className="flex flex-col gap-4">
          {additionalProtectionAddons.map((item) => (
            <div
              className={cn(
                'flex items-center gap-2',
                [
                  TIRE_AND_WINDSHIELD_PROTECTION_ADDON_ID,
                  MINOR_DAMAGE_PROTECTION_ADDON_ID,
                ].includes(item.addon_id) &&
                  isZeroDeductible &&
                  'pointer-events-none opacity-50'
              )}
              key={item.addon_id}
            >
              <Checkbox
                checked={additionalProtections.includes(item.addon_id)}
                name={item.addon_id}
                onCheckedChange={(checked) => {
                  track(TrackingEventName.CHECKBOX_CLICKED, {
                    location: `Addon - ${item.addon_id}`,
                    additionalProps: {
                      checked,
                    },
                  });
                  if (checked) {
                    setAdditionalProtections([
                      ...new Set([...additionalProtections, item.addon_id]),
                    ]);
                  } else {
                    setAdditionalProtections(
                      additionalProtections.filter((p) => p !== item.addon_id)
                    );
                  }
                }}
                id={item.addon_id}
                label={
                  <div className="flex flex-row gap-1 pb-3 pt-2">
                    <p className="body-14-semibold">
                      {item.display_info?.title}
                    </p>
                    <p className="grow basis-1/3 text-right body-14-semibold">
                      {intl.formatMessage(
                        `experiments.additionalProtection.${item.addon_id}.priceLine`,
                        {
                          amount: formatEuro(
                            item?.pricing?.amount?.gross,
                            intl
                          ),
                        }
                      )}
                    </p>
                  </div>
                }
                subtitle={item.display_info?.description}
                subtitleClassName="!body-14-light"
              />
            </div>
          ))}
        </div>
      </div>
    </TrackComponentView>
  );
};
